// Карта мира и геометрия (общие для сервера и клиента). Суша, море, реки, мосты, горы и леса берутся
// из нарисованной карты archipelago-v7 (archipelago.ts); seed влияет только на расстановку объектов.
// Пустые клетки не хранятся в БД: terrain_at(x, y) вычисляется детерминированно.
import {
  CELL,
  MAP_BRIDGES,
  NATIVE,
  archipelagoGrid,
  archipelagoSlots,
  cellAt,
  isNavigableCell,
  isWalkableCell,
  objectiveTile,
  worldRows,
} from './archipelago.js';
import type { EconomyBalance, PoliticsBalance, WorldsBalance } from './balance.js';
import { XorShift128, hashCoord } from './rng.js';
import { type Point, TERRAINS, type Terrain } from './types.js';

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const u = smooth(x - xi);
  const v = smooth(y - yi);
  const a = hashCoord(xi, yi, seed);
  const b = hashCoord(xi + 1, yi, seed);
  const c = hashCoord(xi, yi + 1, seed);
  const d = hashCoord(xi + 1, yi + 1, seed);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

/** Фрактальный шум 0…1 (4 октавы). */
export function fbm(x: number, y: number, seed: number): number {
  let sum = 0;
  let amp = 0.5;
  let freq = 1;
  let norm = 0;
  for (let o = 0; o < 4; o++) {
    sum += valueNoise(x * freq, y * freq, seed + o * 1013) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

/** Клетка внутри нарисованного мира (мир шириной size клеток и высотой worldRows(size)). */
export function inWorld(x: number, y: number, size: number): boolean {
  return x >= 0 && y >= 0 && x < size && y < worldRows(size);
}

/** Местность для каждого кода клетки (CELL → Terrain). */
const CELL_TERRAIN: Terrain[] = [];
CELL_TERRAIN[CELL.SEA] = 'sea';
CELL_TERRAIN[CELL.LAND] = 'plain';
CELL_TERRAIN[CELL.COAST] = 'coast';
CELL_TERRAIN[CELL.RIVER] = 'river';
CELL_TERRAIN[CELL.MOUNTAIN] = 'mountain';
CELL_TERRAIN[CELL.BRIDGE] = 'bridge';
CELL_TERRAIN[CELL.CROSSING] = 'bridge';
CELL_TERRAIN[CELL.FOREST] = 'forest';
CELL_TERRAIN[CELL.ROAD] = 'road';
CELL_TERRAIN[CELL.CLIFF] = 'mountain';
CELL_TERRAIN[CELL.HILL] = 'hill';
CELL_TERRAIN[CELL.PLAZA] = 'road';
CELL_TERRAIN[CELL.OUTSIDE] = 'sea';

function landSamples(x: number, y: number, size: number): number {
  if (!inWorld(x, y, size)) return 0;
  return archipelagoGrid(size).land[y * size + x]!;
}

/**
 * Суша ли клетка на нарисованной карте: больше половины из 3×3 точек клетки — остров.
 * За краем мира — не вода (туда не ставятся морские объекты и не плавают корабли).
 */
export function isPaintedLand(x: number, y: number, size: number): boolean {
  if (!inWorld(x, y, size)) return true;
  return landSamples(x, y, size) >= 5;
}

/**
 * Открытая вода: вся клетка — море, без кромки островов и мостов. Сюда ставятся морские объекты
 * (пиратские бухты, клады), чтобы корабль никогда не стоял на траве.
 */
export function isOpenWater(x: number, y: number, size: number): boolean {
  if (cellAt(x, y, size) !== CELL.SEA || landSamples(x, y, size) !== 0) return false;
  // И соседи — море: у самой кромки мелководье выглядит пляжем, корабль там казался стоящим на песке
  for (let dy = -1; dy <= 1; dy++)
    for (let dx = -1; dx <= 1; dx++)
      if ((dx || dy) && inWorld(x + dx, y + dy, size) && cellAt(x + dx, y + dy, size) !== CELL.SEA)
        return false;
  return true;
}

/** Сплошная суша: вся клетка нарисована землёй (без пляжной кромки и отмели). */
export function isSolidLand(x: number, y: number, size: number): boolean {
  return landSamples(x, y, size) === 9;
}

/** Море (без мостов и края мира). */
export function isSeaAt(x: number, y: number, _seed: number, size: number): boolean {
  return cellAt(x, y, size) === CELL.SEA;
}

/** terrain_at(x, y) — тип местности клетки (суша, берег, лес, дорога, река, гора, мост, море). */
export function terrainAt(x: number, y: number, _seed: number, size: number): Terrain {
  return CELL_TERRAIN[cellAt(x, y, size)] ?? 'sea';
}

/** Вся карта мира одним массивом (индекс = y * size + x, значение = индекс в TERRAINS). */
export function generateTerrainGrid(_seed: number, size: number): Uint8Array {
  return terrainGridFromCells(archipelagoGrid(size).cells, size);
}

/**
 * Сетка клеток archipelago (CELL.*: 0 — море, 1 — суша…) → сетка индексов TERRAINS для terrainFromGrid.
 * Коды разные: без перевода море (CELL.SEA = 0) читалось бы как TERRAINS[0] = 'plain'.
 */
export function terrainGridFromCells(cells: Uint8Array, size: number): Uint8Array {
  const out = new Uint8Array(size * size).fill(TERRAINS.indexOf('sea'));
  const index = CELL_TERRAIN.map((t) => TERRAINS.indexOf(t));
  for (let i = 0; i < cells.length && i < out.length; i++)
    out[i] = index[cells[i]!] ?? TERRAINS.indexOf('sea');
  return out;
}

export function terrainFromGrid(grid: Uint8Array, size: number, x: number, y: number): Terrain {
  if (x < 0 || y < 0 || x >= size || y >= size) return 'sea';
  return TERRAINS[grid[y * size + x] ?? 0] ?? 'sea';
}

export function isBuildableTerrain(t: Terrain): boolean {
  // Склоны (hill) и обрывы — не для замков: строят на ровной земле, в том числе на вершинах плато
  return t === 'plain' || t === 'forest' || t === 'desert' || t === 'snow';
}

export function mapSizeFor(maxPlayers: number, worlds: WorldsBalance): number {
  const keys = Object.keys(worlds.sizes)
    .map(Number)
    .sort((a, b) => a - b);
  for (const k of keys) if (maxPlayers <= k) return worlds.sizes[String(k)] ?? 200;
  return worlds.sizes[String(keys[keys.length - 1])] ?? 300;
}

/** Столица мира — место силы в центре карты. */
export function capitalOf(size: number): Point {
  return objectiveTile(size);
}

export function regionOf(x: number, y: number, size: number, regionsPerSide: number): number {
  const regionSize = Math.ceil(size / regionsPerSide);
  return Math.floor(y / regionSize) * regionsPerSide + Math.floor(x / regionSize);
}

export function distance(a: Point, b: Point): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/** Ближайшая к точке клетка суши (спираль поиска), подходящая для строительства. */
export function nearestBuildable(p: Point, seed: number, size: number, maxRadius = 12): Point | null {
  for (let r = 0; r <= maxRadius; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = p.x + dx;
        const y = p.y + dy;
        if (isBuildableTerrain(terrainAt(x, y, seed, size))) return { x, y };
      }
    }
  }
  return null;
}

const mainlandCache = new Map<number, Uint8Array>();
/**
 * Основная суша: самый большой участок, связанный по суше и мостам. Острова без мостов (дальний остров и
 * островки под замок с гаванью) — не основная суша: туда только морем, игроки по умолчанию там не селятся.
 */
export function isMainland(x: number, y: number, size: number): boolean {
  if (!inWorld(x, y, size)) return false;
  let m = mainlandCache.get(size);
  if (!m) {
    const rows = worldRows(size);
    const n = size * rows;
    const lab = new Int32Array(n).fill(-1);
    const sizes: number[] = [];
    const queue = new Int32Array(n);
    for (let i = 0; i < n; i++) {
      if (lab[i] !== -1 || !isWalkableCell(cellAt(i % size, (i - (i % size)) / size, size))) continue;
      const id = sizes.length;
      let head = 0;
      let tail = 0;
      queue[tail++] = i;
      lab[i] = id;
      let count = 0;
      while (head < tail) {
        const j = queue[head++]!;
        count++;
        const jx = j % size;
        const jy = (j - jx) / size;
        for (const [dx, dy] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ] as const) {
          const nx = jx + dx;
          const ny = jy + dy;
          if (nx < 0 || ny < 0 || nx >= size || ny >= rows) continue;
          const k = ny * size + nx;
          if (lab[k] !== -1 || !isWalkableCell(cellAt(nx, ny, size))) continue;
          lab[k] = id;
          queue[tail++] = k;
        }
      }
      sizes.push(count);
    }
    const main = sizes.indexOf(Math.max(...sizes));
    m = new Uint8Array(n);
    for (let i = 0; i < n; i++) m[i] = lab[i] === main ? 1 : 0;
    mainlandCache.set(size, m);
  }
  return m[y * size + x] === 1;
}

/** Клетка ближе r клеток к оси моста (на съезде с моста ничего не ставим — объект «стоит на мосту»). */
export function nearBridge(x: number, y: number, size: number, r: number): boolean {
  const k = size / NATIVE.width;
  const px = (x + 0.5) / k;
  const py = (y + 0.5) / k;
  for (const b of MAP_BRIDGES) {
    const [x0, y0, x1, y1] = b as [number, number, number, number];
    const vx = x1 - x0;
    const vy = y1 - y0;
    const t = Math.max(0, Math.min(1, ((px - x0) * vx + (py - y0) * vy) / (vx * vx + vy * vy || 1)));
    if (Math.hypot(px - (x0 + t * vx), py - (y0 + t * vy)) * k <= r) return true;
  }
  return false;
}

/** Вода у берега (суша в пределах 2 клеток, сама клетка — море): маяки, фактории, артели стоят у берега. */
export function isShoreWater(x: number, y: number, size: number): boolean {
  if (cellAt(x, y, size) !== CELL.SEA || landSamples(x, y, size) !== 0) return false;
  let land = false;
  for (let dy = -2; dy <= 2; dy++)
    for (let dx = -2; dx <= 2; dx++) {
      const c = cellAt(x + dx, y + dy, size);
      if (c === CELL.BRIDGE || c === CELL.CROSSING) return false;
      if (Math.max(Math.abs(dx), Math.abs(dy)) <= 1 && c !== CELL.SEA) return false; // не вплотную к песку
      if (c !== CELL.SEA && c !== CELL.OUTSIDE && inWorld(x + dx, y + dy, size)) land = true;
    }
  return land;
}

/** Ключевые точки (крепости) вокруг столицы — контроль даёт influence. */
export function keyPointsOf(seed: number, size: number, politics: PoliticsBalance): Point[] {
  const c = capitalOf(size);
  const radius = Math.round(size * politics.keyPoints.radiusShare);
  const res: Point[] = [];
  for (let i = 0; i < politics.keyPoints.count; i++) {
    const angle = (i / politics.keyPoints.count) * Math.PI * 2 + Math.PI / 8;
    const p = {
      x: Math.round(c.x + Math.cos(angle) * radius),
      y: Math.round(c.y + Math.sin(angle) * radius),
    };
    // Ближайшая свободная основная суша, не у моста
    let land: Point | null = null;
    for (let r = 0; r <= 40 && !land; r++)
      for (let dy = -r; dy <= r && !land; dy++)
        for (let dx = -r; dx <= r && !land; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const q = { x: p.x + dx, y: p.y + dy };
          if (
            isBuildableTerrain(terrainAt(q.x, q.y, seed, size)) &&
            isWalkableCell(cellAt(q.x, q.y, size)) &&
            isMainland(q.x, q.y, size) &&
            !nearBridge(q.x, q.y, size, 4)
          )
            land = q;
        }
    if (land) res.push(land);
  }
  return res;
}

/** Honeypot-клетки: фейковые замки. Сервер не отдаёт их в /map; поход туда = признак бота/читов. */
export function honeypotsOf(seed: number, size: number, count: number): Point[] {
  const rng = new XorShift128(`honeypot:${seed}`);
  const c = capitalOf(size);
  const res: Point[] = [];
  let guard = 0;
  while (res.length < count && guard++ < count * 200) {
    const x = rng.int(5, size - 6);
    const y = rng.int(5, worldRows(size) - 6);
    if (distance({ x, y }, c) < 12) continue;
    if (!isBuildableTerrain(terrainAt(x, y, seed, size))) continue;
    if (res.some((p) => distance(p, { x, y }) < 8)) continue;
    res.push({ x, y });
  }
  return res;
}

export function isHoneypot(p: Point, honeypots: Point[]): boolean {
  return honeypots.some((h) => h.x === p.x && h.y === p.y);
}

const spawnCache = new Map<number, Point[]>();

/**
 * Порядок заселения мира: места замков из раскладки карты — от начала координат (0, 0), с первой
 * суши, всё дальше; затем (если места кончились) — любая свободная суша в том же порядке.
 * Новые игроки расходятся спиралью по кругу карты: на разные острова, сначала у края (там всё слабее).
 */
export function spawnOrder(size: number): Point[] {
  const hit = spawnCache.get(size);
  if (hit) return hit;
  // Только основная суша: на острова без мостов игроки по умолчанию не селятся
  const slots = archipelagoSlots(size, 'player').filter(
    (p) => isMainland(p.x, p.y, size) && !nearBridge(p.x, p.y, size, 3),
  );
  const extra: Point[] = [];
  const rows = worldRows(size);
  for (let y = 2; y < rows - 2; y += 3)
    for (let x = 2; x < size - 2; x += 3) {
      if (!isBuildableTerrain(terrainAt(x, y, 0, size)) || !isWalkableCell(cellAt(x, y, size))) continue;
      if (!isMainland(x, y, size) || nearBridge(x, y, size, 3)) continue;
      if (slots.some((p) => Math.abs(p.x - x) <= 2 && Math.abs(p.y - y) <= 2)) continue;
      extra.push({ x, y });
    }
  const res = [...spiralOrder(slots, size), ...spiralOrder(extra, size)];
  spawnCache.set(size, res);
  return res;
}

const SPIRAL_SECTORS = 8;

/** Спираль поселения: по кругу карты сектор за сектором, в каждом — от края к центру. */
function spiralOrder(points: Point[], size: number): Point[] {
  const c = capitalOf(size);
  const rows = worldRows(size);
  const sectors: Point[][] = Array.from({ length: SPIRAL_SECTORS }, () => []);
  for (const p of points) {
    const angle = Math.atan2((p.y - c.y) / rows, (p.x - c.x) / size) + Math.PI;
    sectors[Math.min(SPIRAL_SECTORS - 1, Math.floor((angle / (2 * Math.PI)) * SPIRAL_SECTORS))]!.push(p);
  }
  for (const s of sectors) s.sort((a, b) => edgeRatio(b, size) - edgeRatio(a, size));
  const res: Point[] = [];
  for (let i = 0; res.length < points.length; i++)
    for (const s of sectors) {
      const p = s[i];
      if (p) res.push(p);
    }
  return res;
}

const REFERENCE_SPEED = 10;

/**
 * Время по полной стоимости маршрута и скорости самого медленного отряда.
 * secondsPerTile задаёт время клетки равнины для скорости 10. Дороги/леса уже
 * учтены в dist. Бонусы увеличивают скорость; ограничений длины и времени нет.
 */
export function travelSeconds(
  dist: number,
  slowestSpeed: number,
  speedBonusPct: number,
  march: EconomyBalance['march'],
): number {
  const speed = Math.max(1, slowestSpeed) * (1 + speedBonusPct / 100);
  return Math.max(1, Math.round((dist * march.secondsPerTile * REFERENCE_SPEED) / speed));
}

/**
 * Насколько точка у края карты: 0 — столица в центре, 1 — край. Карта вытянута, поэтому считается по
 * эллипсу: край сверху и снизу тоже даёт 1, а не только дальние концы по ширине.
 */
export function edgeRatio(p: Point, size: number): number {
  const c = capitalOf(size);
  const rows = worldRows(size);
  const a = Math.max(c.x, size - c.x);
  const b = Math.max(c.y, rows - c.y);
  return Math.min(1, Math.hypot((p.x - c.x) / a, (p.y - c.y) / b));
}

/** Уровень объекта мира по месту: по краям карты низкий, ближе к центру — высокий. */
export function abandonedLevelAt(p: Point, size: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(max - (max - min) * edgeRatio(p, size))));
}

/**
 * Место морской высадки: клетка моря у берега (рядом есть суша) не дальше maxDist от цели;
 * ближе к цели, при равенстве — ближе к гавани отправления. null — цель слишком далеко от моря.
 */
export function landingSpot(
  seed: number,
  size: number,
  target: Point,
  from: Point,
  maxDist: number,
): Point | null {
  let best: Point | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (let dy = -maxDist; dy <= maxDist; dy++) {
    for (let dx = -maxDist; dx <= maxDist; dx++) {
      const x = target.x + dx;
      const y = target.y + dy;
      const d = Math.hypot(dx, dy);
      if (d > maxDist || !inWorld(x, y, size)) continue;
      if (!isSeaAt(x, y, seed, size)) continue;
      const nearLand =
        !isSeaAt(x + 1, y, seed, size) ||
        !isSeaAt(x - 1, y, seed, size) ||
        !isSeaAt(x, y + 1, seed, size) ||
        !isSeaAt(x, y - 1, seed, size);
      if (!nearLand) continue;
      const score = d * 1000 + distance(from, { x, y });
      if (score < bestScore) {
        bestScore = score;
        best = { x, y };
      }
    }
  }
  return best;
}

const seaDistCache = new Map<number, Uint16Array>();

/**
 * Расстояние (в клетках, шаги в 8 направлениях) от клетки до ближайшего моря.
 * Поле считается один раз на размер мира (поиск в ширину от всех морских клеток).
 */
export function distanceToSea(x: number, y: number, size: number): number {
  const rows = worldRows(size);
  let grid = seaDistCache.get(size);
  if (!grid) {
    const cells = archipelagoGrid(size).cells;
    grid = new Uint16Array(size * rows).fill(0xffff);
    const queue = new Int32Array(size * rows);
    let head = 0;
    let tail = 0;
    for (let i = 0; i < cells.length; i++)
      if (cells[i] === CELL.SEA) {
        grid[i] = 0;
        queue[tail++] = i;
      }
    while (head < tail) {
      const i = queue[head++]!;
      const cx = i % size;
      const cy = (i - cx) / size;
      const d = grid[i]! + 1;
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) {
          const nx = cx + dx;
          const ny = cy + dy;
          if (nx < 0 || ny < 0 || nx >= size || ny >= rows) continue;
          const j = ny * size + nx;
          if (grid[j]! > d) {
            grid[j] = d;
            queue[tail++] = j;
          }
        }
    }
    seaDistCache.set(size, grid);
  }
  if (!inWorld(x, y, size)) return 0;
  return grid[y * size + x]!;
}

const areaCache = new Map<number, number>();
const seaAreaCache = new Map<number, number>();

/** Площадь моря мира в клетках² (без мостов и края мира) — от неё считается число морских объектов. */
export function seaArea(size: number): number {
  let a = seaAreaCache.get(size);
  if (a == null) {
    a = 0;
    for (const c of archipelagoGrid(size).cells) if (c === CELL.SEA) a++;
    seaAreaCache.set(size, a);
  }
  return a;
}

/** Площадь суши мира в клетках² — от неё считается число объектов на суше. */
export function islandArea(size: number): number {
  let a = areaCache.get(size);
  if (a == null) {
    a = 0;
    for (const c of archipelagoGrid(size).cells) if (c !== CELL.SEA && c !== CELL.BRIDGE) a++;
    areaCache.set(size, a);
  }
  return a;
}

// ───── Морские маршруты: корабли идут только по воде, огибая сушу ─────

const seaPathCache = new Map<string, Point[]>();
/** Пары точек, между которыми по воде не доплыть (seaPath тогда отдаёт прямую линию). */
const seaUnreachable = new Set<string>();

/**
 * Путь корабля по воде между двумя клетками (поиск A* по 8 направлениям, диагональ не «срезает» угол
 * суши). Начало и конец могут стоять на берегу (гавань, порт) — остальной путь только по морю.
 * Возвращает упрощённую ломаную (точки поворота), включая концы. Нет пути — прямая (страховка).
 */
export function seaPath(size: number, from: Point, to: Point): Point[] {
  const key = `${size}:${Math.round(from.x)},${Math.round(from.y)}>${Math.round(to.x)},${Math.round(to.y)}`;
  const cached = seaPathCache.get(key);
  if (cached) return cached;
  const rows = worldRows(size);
  const cells = archipelagoGrid(size).cells;
  const inside = (x: number, y: number) => x >= 0 && y >= 0 && x < size && y < rows;
  // Гавань стоит на берегу (суша): корабли выходят из ближайшей к ней клетки воды и туда же возвращаются
  const snap = (p: Point): Point => {
    const x0 = Math.round(p.x);
    const y0 = Math.round(p.y);
    const nav = (x: number, y: number) => inside(x, y) && isNavigableCell(cells[y * size + x]!);
    if (nav(x0, y0)) return { x: x0, y: y0 };
    for (let r = 1; r <= 4; r++) {
      let best: Point | null = null;
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r || !nav(x0 + dx, y0 + dy)) continue;
          if (!best || Math.hypot(dx, dy) < Math.hypot(best.x - x0, best.y - y0))
            best = { x: x0 + dx, y: y0 + dy };
        }
      if (best) return best;
    }
    return { x: x0, y: y0 };
  };
  const start = snap(from);
  const end = snap(to);
  const fx = start.x;
  const fy = start.y;
  const tx = end.x;
  const ty = end.y;
  // Вода — море и проход под длинными мостами; реки несудоходны
  const water = (x: number, y: number) =>
    inside(x, y) &&
    (isNavigableCell(cells[y * size + x]!) || (x === fx && y === fy) || (x === tx && y === ty));
  const result = (() => {
    if (fx === tx && fy === ty) return [{ x: fx, y: fy }];
    const n = size * rows;
    const g = new Float32Array(n).fill(Number.POSITIVE_INFINITY);
    const prev = new Int32Array(n).fill(-1);
    const closed = new Uint8Array(n);
    // Двоичная куча по f = g + эвристика (октильное расстояние)
    const heap: number[] = [];
    const f = new Float32Array(n);
    const h = (x: number, y: number) => {
      const dx = Math.abs(x - tx);
      const dy = Math.abs(y - ty);
      return Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy);
    };
    const push = (i: number) => {
      heap.push(i);
      let c = heap.length - 1;
      while (c > 0) {
        const p = (c - 1) >> 1;
        if (f[heap[p]!]! <= f[heap[c]!]!) break;
        [heap[p], heap[c]] = [heap[c]!, heap[p]!];
        c = p;
      }
    };
    const pop = () => {
      const top = heap[0]!;
      const last = heap.pop()!;
      if (heap.length) {
        heap[0] = last;
        let c = 0;
        for (;;) {
          const l = c * 2 + 1;
          const r = l + 1;
          let m = c;
          if (l < heap.length && f[heap[l]!]! < f[heap[m]!]!) m = l;
          if (r < heap.length && f[heap[r]!]! < f[heap[m]!]!) m = r;
          if (m === c) break;
          [heap[m], heap[c]] = [heap[c]!, heap[m]!];
          c = m;
        }
      }
      return top;
    };
    const start = fy * size + fx;
    const goal = ty * size + tx;
    g[start] = 0;
    f[start] = h(fx, fy);
    push(start);
    while (heap.length) {
      const cur = pop();
      if (cur === goal) break;
      if (closed[cur]) continue;
      closed[cur] = 1;
      const cx = cur % size;
      const cy = (cur - cx) / size;
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          const nx = cx + dx;
          const ny = cy + dy;
          if (!water(nx, ny)) continue;
          // По диагонали — только если обе соседние клетки тоже вода (не срезаем мыс)
          if (dx && dy && (!water(cx + dx, cy) || !water(cx, cy + dy))) continue;
          const j = ny * size + nx;
          const ng = g[cur]! + (dx && dy ? Math.SQRT2 : 1);
          if (ng < g[j]!) {
            g[j] = ng;
            prev[j] = cur;
            f[j] = ng + h(nx, ny);
            push(j);
          }
        }
    }
    if (prev[goal] === -1) {
      if (seaUnreachable.size > 2000) seaUnreachable.clear();
      seaUnreachable.add(key);
      return [
        { x: fx, y: fy },
        { x: tx, y: ty },
      ];
    }
    const cells: Point[] = [];
    for (let i = goal; i !== -1; i = prev[i]!) cells.push({ x: i % size, y: Math.floor(i / size) });
    cells.reverse();
    // Упрощение: оставляем клетку, если прямая к следующей опорной идёт только по воде
    const out: Point[] = [cells[0]!];
    let anchor = 0;
    for (let i = 2; i < cells.length; i++) {
      if (!straightWater(cells[anchor]!, cells[i]!, water)) {
        out.push(cells[i - 1]!);
        anchor = i - 1;
      }
    }
    out.push(cells[cells.length - 1]!);
    return out;
  })();
  seaPathCache.set(key, result);
  if (seaPathCache.size > 400) seaPathCache.delete(seaPathCache.keys().next().value!);
  return result;
}

function straightWater(a: Point, b: Point, water: (x: number, y: number) => boolean): boolean {
  const steps = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) * 3);
  for (let s = 1; s < steps; s++) {
    const k = s / steps;
    const x = a.x + (b.x - a.x) * k;
    const y = a.y + (b.y - a.y) * k;
    if (!water(Math.round(x), Math.round(y))) return false;
    // Не касаемся углов суши: проверяем и соседние клетки отрезка
    if (!water(Math.floor(x), Math.floor(y)) || !water(Math.ceil(x), Math.ceil(y))) return false;
  }
  return true;
}

/** Можно ли доплыть по воде (от гавани — от ближайшей к ней клетки моря). */
export function seaReachable(size: number, from: Point, to: Point): boolean {
  seaPath(size, from, to);
  return !seaUnreachable.has(
    `${size}:${Math.round(from.x)},${Math.round(from.y)}>${Math.round(to.x)},${Math.round(to.y)}`,
  );
}

/** Длина морского пути (в клетках) — от неё считается время плавания. */
export function seaPathLength(size: number, from: Point, to: Point): number {
  const p = seaPath(size, from, to);
  let len = 0;
  for (let i = 1; i < p.length; i++) len += Math.hypot(p[i]!.x - p[i - 1]!.x, p[i]!.y - p[i - 1]!.y);
  return len;
}

/** Точка на морском пути по доле 0…1 и направление движения. */
export function seaPathPoint(
  size: number,
  from: Point,
  to: Point,
  k: number,
): Point & { dx: number; dy: number } {
  const p = seaPath(size, from, to);
  if (p.length < 2) return { ...p[0]!, dx: 1, dy: 0 };
  const total = seaPathLength(size, from, to);
  let d = Math.max(0, Math.min(1, k)) * total;
  for (let i = 1; i < p.length; i++) {
    const a = p[i - 1]!;
    const b = p[i]!;
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    if (d <= seg || i === p.length - 1) {
      const f = seg > 0 ? Math.min(1, d / seg) : 0;
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, dx: b.x - a.x, dy: b.y - a.y };
    }
    d -= seg;
  }
  const last = p[p.length - 1]!;
  return { ...last, dx: 1, dy: 0 };
}
